/**
 * Money + quantity formatting helpers.
 *
 * Money values travel over the API as numeric(18,6) strings; the UI
 * formats them for display with Intl.NumberFormat. No float arithmetic —
 * formatting only.
 *
 * Locale and time zone: every function defaults to the interface's
 * (lib/i18n/README.md — the person's own locale, else the workspace's
 * conventions in the interface's language; the person's or workspace's
 * zone), read at call time, so no call site passes them. An explicit `locale` argument still
 * wins. Arabic locales format with Latin digits and the Gregorian calendar
 * unless the tag says otherwise (see lib/i18n/locale.ts for why).
 */
import { getFormatSettings } from "@/lib/i18n/core";
import { formatLocaleOf } from "@/lib/i18n/locale";

/** The formatter tag: the caller's locale if given, else the person's. */
function localeFor(locale: string | undefined): string {
  return locale ? formatLocaleOf(locale) : getFormatSettings().formatLocale;
}

/**
 * A calendar date with no time ("2026-10-05", a posting or due date) is the
 * same day everywhere; formatting it in a zone west of UTC would show the
 * day before. Only instants are converted to the person's zone.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function zoneFor(value: string | Date): string | undefined {
  if (typeof value === "string" && DATE_ONLY.test(value)) return "UTC";
  return getFormatSettings().timeZone;
}

/** The decimal strings the API sends: numeric(18,6) as text, "-12.500000". */
const DECIMAL = /^-?\d+(\.\d+)?$/;
/** An ISO 4217 code as the API sends it. */
const CURRENCY_CODE = /^[A-Z]{3}$/;
/** U+00A4 CURRENCY SIGN: "some currency, not known". */
const UNKNOWN_CURRENCY_SIGN = "¤";

/**
 * A decimal string formatted by Intl as the exact decimal it is.
 *
 * ES2023 Intl.NumberFormat (Chrome 106+, Node 19+) reads a string argument as
 * an exact decimal, so "123456789012345.125" rounds half-expand to …345.13; a
 * Number would have lost the last digits first and shown …345.12. The ES2022
 * lib this app compiles against still types format() as number | bigint, so
 * the call is typed here, once.
 */
function formatDecimalString(nf: Intl.NumberFormat, decimal: string): string {
  return (nf.format as unknown as (value: string) => string)(decimal);
}

/**
 * The amount with no currency style, every digit it has: what is shown when
 * the currency is missing, so nothing is rounded to a guessed currency's
 * digits.
 */
function plainAmount(value: string | number, locale: string): string {
  if (typeof value === "number") {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 20 }).format(
      value,
    );
  }
  // Intl accepts at most 20 fraction digits in older engines; the API sends 6.
  const digits = Math.min(value.split(".")[1]?.length ?? 0, 20);
  return formatDecimalString(
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }),
    value,
  );
}

/**
 * Format an amount in its currency (docs/design/currency.md R7).
 *
 * `currency` has no default, so a call without one does not compile: a
 * currency comes from the record, the figure or the workspace, never from
 * this function. A decimal string from the API is formatted as the exact
 * decimal it is, never through Number(); anything else that is not a plain
 * decimal renders "—". A number (the Home KPI `value`) is formatted as given.
 * Digits and rounding are Intl's for the currency (TND, OMR and JOD get 3).
 *
 * A currency that is not three capital letters at run time (a field the API
 * did not send) is never guessed and never throws: the amount renders as
 * "¤ 1,234.5" and MISSING_CURRENCY is logged in every build, so a screen
 * that loses a currency is visible rather than shown in dollars.
 */
export function formatMoney(
  value: string | number | null | undefined,
  currency: string,
  locale?: string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && !DECIMAL.test(value)) return "—";
  if (typeof value === "number" && !Number.isFinite(value)) return "—";
  const tag = localeFor(locale);
  if (typeof currency !== "string" || !CURRENCY_CODE.test(currency)) {
    console.error("MISSING_CURRENCY", currency);
    return `${UNKNOWN_CURRENCY_SIGN} ${plainAmount(value, tag)}`;
  }
  const nf = new Intl.NumberFormat(tag, { style: "currency", currency });
  return typeof value === "string"
    ? formatDecimalString(nf, value)
    : nf.format(value);
}

export function formatQuantity(
  value: string | number | null | undefined,
  decimalPlaces = 3,
  locale?: string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat(localeFor(locale), {
    maximumFractionDigits: decimalPlaces,
  }).format(num);
}

export function formatDate(
  value: string | Date | null | undefined,
  locale?: string,
): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(localeFor(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: zoneFor(value),
  }).format(d);
}

export function formatDateTime(
  value: string | Date | null | undefined,
  locale?: string,
): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(localeFor(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: zoneFor(value),
  }).format(d);
}

/**
 * Format a PERCENTAGE — a number already on the 0-100 scale, not a ratio.
 *
 * Every caller passes one: SupplierEvaluation stores on-time delivery,
 * quality and price variance as numeric(5,2) constrained to 0-100, so 92.50
 * means 92.5%. Intl's percent style multiplies by 100, which is why the value
 * is divided by 100 first; the two cancel and the number is displayed as
 * given, with a '%' and locale grouping.
 */
export function formatPercent(
  value: string | number | null | undefined,
  locale?: string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat(localeFor(locale), {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(num / 100);
}
